// Completion form for admin-published profile requirements (the pre-snapshot
// data gate). Renders one input per unmet requirement: profile-field ones save
// through the normal my-profile update, custom questions through the answers
// endpoint. Shared by the dashboard banner modal and the snapshot-wizard gate.

import { useState } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { profileRequirementsAPI, profilesAPI } from '../api/client';
import type { ProfileRequirement, UserProfile } from '../types';

interface Props {
  requirements: ProfileRequirement[];
  // Called once every requirement has been satisfied and verified server-side.
  onCompleted: () => void;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent';

export default function ProfileRequirementsForm({ requirements, onCompleted }: Props) {
  const [values, setValues] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const setValue = (id: number, value: string) =>
    setValues((prev) => ({ ...prev, [id]: value }));

  const allFilled = requirements.every((r) => (values[r.id] ?? '').trim() !== '');

  const handleSave = async () => {
    if (!allFilled || isSaving) return;
    setIsSaving(true);
    try {
      const profilePatch: Record<string, string | number> = {};
      const answers: { id: number; text: string }[] = [];

      for (const req of requirements) {
        const raw = (values[req.id] ?? '').trim();
        if (req.kind === 'profile_field') {
          profilePatch[req.field_name] =
            req.field_meta?.type === 'number' ? Number(raw) : raw;
        } else {
          answers.push({ id: req.id, text: raw });
        }
      }

      if (Object.keys(profilePatch).length > 0) {
        await profilesAPI.updateMyProfile(profilePatch as unknown as Partial<UserProfile>);
      }
      for (const answer of answers) {
        await profileRequirementsAPI.submitAnswer(answer.id, answer.text);
      }

      // Confirm with the server so the banner/gate only clears when truly met.
      const remaining = await profileRequirementsAPI.getMyUnmet();
      if (remaining.length === 0) {
        toast.success('Thank you! Your info is all set.');
        onCompleted();
      } else {
        toast.error('Some items could not be saved. Please try again.');
      }
    } catch {
      toast.error('Could not save your info. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {requirements.map((req) => {
        const label =
          req.kind === 'profile_field' ? req.field_label : req.question_text;
        const meta = req.field_meta;
        return (
          <div key={req.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
            </label>
            {req.kind === 'custom_question' ? (
              <textarea
                value={values[req.id] ?? ''}
                onChange={(e) => setValue(req.id, e.target.value)}
                rows={3}
                className={inputClass}
                placeholder="Type your answer..."
              />
            ) : meta?.type === 'select' ? (
              <select
                value={values[req.id] ?? ''}
                onChange={(e) => setValue(req.id, e.target.value)}
                className={inputClass}
              >
                <option value="">Select...</option>
                {(meta.choices ?? []).map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={meta?.type === 'number' ? 'number' : 'text'}
                min={meta?.type === 'number' ? 0 : undefined}
                value={values[req.id] ?? ''}
                onChange={(e) => setValue(req.id, e.target.value)}
                className={inputClass}
              />
            )}
          </div>
        );
      })}

      <button
        onClick={handleSave}
        disabled={!allFilled || isSaving}
        className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save size={18} />
        {isSaving ? 'Saving...' : 'Save my info'}
      </button>
    </div>
  );
}
